// @flow

import * as React from 'react';

import get from 'lodash/get';
import toPairs from 'lodash/toPairs';
import {BodyMono} from 'sail/Text';
import {Executable} from './_executable';
import {tree} from '../generated_tree';
import {resource} from '../commands/resource';
import type {Command, Commands} from '../helpers/commands/resource';
import {parseParams} from '../helpers/cli/general';
import {listen} from '../commands/listen';
import {trigger, TriggerHelp} from '../commands/trigger';
import Help from '../commands/help';
import {Missing} from '../commands/missing';

export class Stripe extends Executable {
  commands = {listen, trigger};

  missing: Set<string> = new Set([
    'plugin',
    'apps',
    'get',
    'post',
    'delete',
    'feedback',
    'community',
    'logs',
    'status',
    'completion',
    'config',
    'open',
    'samples',
    'serve',
    'version',
    'login',
    'logout',
  ]);

  args: Array<string>;

  commandName(): string {
    return 'stripe';
  }

  shortHelp(): string {
    return 'available Stripe CLI commands';
  }

  help(): React.Node {
    if (this.missing.has(this.command.structuredArgs[0])) {
      this.setAllowInput(true);
      return <Missing />;
    }

    const {command, commandPath} = findCommand(this.command.structuredArgs);

    // We couldn't find a match command from the generated tree so it might
    // not exist. Return an error
    //
    // It would be amazing if this implemented a "did you mean" feature.
    if (command === null && commandPath !== 'help') {
      this.setAllowInput(true);
      return (
        <BodyMono color="gray200">
          stripe command not found: {commandPath.replace('.', ' ')}
        </BodyMono>
      );
    }

    if (command === null || commandPath === 'help') {
      this.setAllowInput(true);
      return <Help />;
    }

    if (commandPath === 'trigger') {
      this.setAllowInput(true);
      return <TriggerHelp />;
    }

    const node: Command = command;
    let output = `Usage:\n  ${this.command.renderInvocation()} `;

    if (node && node.commands && typeof node.commands === 'object') {
      output += `<operation> [parameters...]\n\n`;
      output += `Commands:\n  ${Object.keys(node.commands).join('\n  ')} \n`;
    }
    if (node && node.params && typeof node.params === 'object') {
      const hasArg = !!(node?.args?.length ?? 0);
      output += `${hasArg ? '<argument> ' : ''}[parameters...]\n\n`;
      output += `Parameters:\n  ${Object.keys(node.params ?? {})
        .map((p) => `--${p}`)
        .join('\n  ')} \n`;
    }

    this.setAllowInput(true);
    return output;
  }

  run(apiKey: string) {
    let params = {};

    // The user ran `stripe` with no arguments, which should return
    // the help text
    if (this.command.structuredArgs.length === 0) {
      this.setAllowInput(true);
      return <Help />;
    }

    // User explicitly ran a command that is not currently supported
    if (this.missing.has(this.command.structuredArgs[0])) {
      this.setAllowInput(true);
      return <Missing />;
    }

    // Recursively search the generated tree for the command the user
    // invoked. Capture the command and then the remaining arguments
    const {command, args, commandPath} = findCommand(
      this.command.structuredArgs,
    );

    // We couldn't find a match command from the generated tree so it might
    // not exist. Return an error
    //
    // It would be amazing if this implemented a "did you mean" feature.
    if (command === null) {
      this.setAllowInput(true);
      return (
        <BodyMono color="gray200">
          stripe command not found: {commandPath.replace('.', ' ')}
        </BodyMono>
      );
    }

    // Try to parse the parameters. If it fails, return an error
    try {
      params = this._parseParams(command);
    } catch (err) {
      return <BodyMono color="gray200">{err.message}</BodyMono>;
    }

    // resource commands
    if (!(commandPath in this.commands)) {
      // If this is not a command that can execute, call the help
      // function
      if (!command.run) {
        return this.help();
      }

      return resource(command, args, params, apiKey, this.setAllowInput);
    }

    return this.commands[commandPath]({
      args,
      params,
      apiKey,
      setAllowInput: this.setAllowInput,
    });
  }

  _parseParams(command: Command): {[string]: string} {
    const possibleParams = {};
    for (const [key, value] of toPairs(
      tree.root.commands?.stripe.params ?? {},
    )) {
      possibleParams[key] = value;
    }
    for (const [key, value] of toPairs(command.params ?? {})) {
      possibleParams[key] = value;
    }

    return parseParams(this.command.structuredFlags, possibleParams, command);
  }
}

export function getRoot(): Command {
  return get(tree, 'root.commands.stripe', {});
}

export function findCommand(
  args: Array<string>,
  root?: Commands,
): {command: Command | null, args: Array<string>, commandPath: string} {
  // Load all possible commands from the generated tree. Allow a
  // potential root tree to be passed in (makes testing easier)
  const commands = get(root, 'root.commands.stripe', null) ?? getRoot();

  return _findCommand(commands, args, '');
}

// Recursively traverse the generated tree to extract the full
// command invoked by the user. Since most shell commands only
// (technically) have 1 top-level command (the executable), we
// will need to recursively parse into the tree, progressively
// popping the next argument and mapping into the command
// structure. (ie: `stripe customers create`, `customers create`
// are args but we need to map them to nested commands
//
// At the end of the tree traversal, we will be left with the
// actual arguments that need to be passed into the command itself.
//
// This also creates a JSON path to the command
// (ie: customers.create) incase that's helpful for debugging or
// accessing later.
function _findCommand(
  commands: Command,
  argsList: Array<string>,
  commandPath: string,
): {command: Command | null, args: Array<string>, commandPath: string} {
  const args = [...argsList];

  // There are no args, we've reached an executable command, or
  // there are no more nodes so return.
  if (args.length === 0 || commands.run === true || !commands.commands) {
    return {command: commands, args, commandPath};
  }

  // Pop the next argument we need to traverse for.
  const arg = args.shift();

  // Update the command path called, ie if we run `stripe customers create`
  // we want to return a command path of `customers.create` to help
  // understand the command invoked
  let updatedPath = commandPath;
  if (updatedPath.length !== 0) {
    updatedPath += '.';
  }
  updatedPath += arg;

  // If there are nested commands and the arg is in the list, dive
  // deeper.
  if (commands.commands && arg in commands.commands) {
    const command: Command = commands.commands[arg];

    return _findCommand(command, args, updatedPath);
  }

  // Fallback, we somehow still have args, are not in a leaf node,
  // but cannot traverse further. This might indicate a bad command
  // like if someone did `stripe customers foo`.
  return {command: null, args, commandPath: updatedPath};
}
